import {
  type ComicPageLayoutTemplate,
  getPageLayoutDefinition,
  layoutGridContainerStyle,
  layoutSlotCss,
} from '../../lib/comicPageLayout'
import { type ComicPanelAspectPreset,panelAspectTailwindClass } from '../../lib/comicProjectSettings'
import type { ComicPanel } from '../../lib/comicTypes'
import { mediaDisplayUrl } from '../../lib/resolvedMediaImage'
import { ThemedImagePlaceholder } from '../ThemedImagePlaceholder'
import { ComicPageLayoutSkeletonOverlay } from './ComicPageLayoutSkeletonOverlay'
import { comicPanelRound } from './comicPanelTokens'

type Props = {
  angular: boolean
  layout: ComicPageLayoutTemplate
  panels: ComicPanel[]
  panelAspect: ComicPanelAspectPreset
  selectedPanelId?: string | null
  onSelectPanel?: (panelId: string) => void
  /** 连续阅读：更紧凑、无选中描边 */
  compact?: boolean
  panelGeneratingId?: string | null
}

export function ComicPageLayoutGrid({
  angular,
  layout,
  panels,
  panelAspect,
  selectedPanelId = null,
  onSelectPanel,
  compact = false,
  panelGeneratingId = null,
}: Props) {
  const def = getPageLayoutDefinition(layout)
  const aspectClass = panelAspectTailwindClass(panelAspect)
  const gapClass = compact ? 'gap-0' : 'gap-0.5 sm:gap-1'
  const minRow = compact ? 'minmax(4.5rem, auto)' : 'minmax(5.5rem, auto)'

  return (
    <div
      className={['grid min-w-0', gapClass].join(' ')}
      style={{
        ...layoutGridContainerStyle(layout),
        gridTemplateRows: `repeat(${def.rows}, ${minRow})`,
      }}
    >
      {def.slots.map((slot, slotIndex) => {
        const panel = panels[slotIndex] ?? null
        const imageUrl = panel?.imageUrl?.trim() ? mediaDisplayUrl(panel.imageUrl) : null
        const selected = panel != null && panel.id === selectedPanelId
        const generating =
          panel != null &&
          (panelGeneratingId === panel.id || panel.status === 'generating')
        return (
          <div key={slotIndex} className="relative min-h-0 min-w-0" style={layoutSlotCss(slot)}>
            <ThemedImagePlaceholder
              as={onSelectPanel && panel ? 'button' : 'div'}
              angular={angular}
              aspectClass={compact ? 'aspect-auto h-full min-h-[4.5rem]' : aspectClass}
              className="h-full w-full"
              loading={!imageUrl}
              selected={!compact && selected}
              onClick={panel && onSelectPanel ? () => onSelectPanel(panel.id) : undefined}
              aria-label={panel ? `分镜格 ${slotIndex + 1}` : `空位 ${slotIndex + 1}`}
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt=""
                  className="absolute inset-0 z-[1] h-full w-full object-cover"
                  draggable={false}
                />
              ) : null}
              {!imageUrl && !generating ? (
                <ComicPageLayoutSkeletonOverlay layout={layout} faint={false} />
              ) : imageUrl ? (
                <ComicPageLayoutSkeletonOverlay layout={layout} faint />
              ) : null}
              {generating ? (
                <div
                  className={[
                    'absolute inset-0 z-[3] flex items-center justify-center bg-[color-mix(in_srgb,var(--panel)_82%,transparent)] text-xs font-semibold text-ink',
                    comicPanelRound(angular),
                  ].join(' ')}
                >
                  生成中…
                </div>
              ) : null}
            </ThemedImagePlaceholder>
          </div>
        )
      })}
    </div>
  )
}
